import { useAdminSender } from "../api";
import { SpotItemRevisionCreate } from "../generatedApi/spotManagerApi";
import { BulkOperationResult, SpotWithGooglePlaceId } from "../types/bulkOperations";
import { SelectedFields } from "../components/GooglePlaceDetailsModal/types";
import {
  calculateRequiredTiers,
  convertToLocalPhoneNumber
} from "../components/GooglePlaceDetailsModal/utils";
import { useGooglePlaces } from "./useGooglePlaces";
import { useDuplicateCheck } from "./useDuplicateCheck";

/**
 * Google一括操作（詳細情報取得、PlaceID更新）のカスタムフック
 */
export const useBulkGoogleOperations = () => {
  const { fetchPlaceDetails, refreshPlaceId } = useGooglePlaces();
  const { checkPlaceDuplicate, fetchCidFromPlaceId } = useDuplicateCheck();

  const { sender: getSpotItemRevision } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "getSpotItemRevision"
  });

  const { sender: createSpotRevision } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "default",
    operationId: "createSpotRevision"
  });

  /**
   * 最新リビジョンを取得する
   */
  const fetchLatestRevision = async (spotId: string, revisionId: string) => {
    try {
      return await getSpotItemRevision({ spotId, revisionId });
    } catch (error) {
      throw new Error(
        `リビジョンの取得に失敗: ${error instanceof Error ? error.message : "不明なエラー"}`
      );
    }
  };

  /**
   * 新リビジョンを作成する
   */
  const createNewRevision = async (spotId: string, requestBody: any) => {
    try {
      await createSpotRevision({ spotId, requestBody });
    } catch (error) {
      throw new Error(
        `リビジョンの作成に失敗: ${error instanceof Error ? error.message : "不明なエラー"}`
      );
    }
  };

  /**
   * Google詳細情報を取得してスポットを更新する
   */
  const performGoogleDetailsUpdate = async (
    spot: SpotWithGooglePlaceId,
    selectedFields: SelectedFields
  ): Promise<BulkOperationResult> => {
    const { spot_id, name, latest_revision_id, google_place_id } = spot;

    try {
      // 1. Google情報取得
      const tiers = calculateRequiredTiers(selectedFields);
      let details;
      try {
        details = await fetchPlaceDetails(google_place_id, tiers);
      } catch (error) {
        throw new Error(
          `Google情報の取得に失敗: ${error instanceof Error ? error.message : "不明なエラー"}`
        );
      }

      // 2. 最新リビジョン取得
      const latestRevision = await fetchLatestRevision(spot_id, latest_revision_id);

      // 3. データマージ
      const filteredData: Partial<SpotItemRevisionCreate> = {};

      if (selectedFields.name && details.name) {
        filteredData.name = details.name;
      }
      if (selectedFields.formattedAddress && details.address) {
        filteredData.address = details.address;
      }
      if (selectedFields.addressComponents && details.address_component) {
        filteredData.address_component = details.address_component;
      }
      if (selectedFields.location && details.location) {
        filteredData.location = details.location;
      }
      if (selectedFields.googleMapsUri && details.google_cid) {
        filteredData.google_cid = details.google_cid;
      }
      if (selectedFields.internationalPhoneNumber && details.phone_number) {
        filteredData.phone_number = convertToLocalPhoneNumber(details.phone_number);
      }
      if (selectedFields.regularOpeningHours && details.opening_hours) {
        filteredData.opening_hours = details.opening_hours.join("\n");
      }
      if (selectedFields.businessStatus && details.business_status) {
        filteredData.business_status = details.business_status;
      }
      if (selectedFields.parkingOptions && details.parking) {
        filteredData.parking = details.parking;
      }

      const mergedData: SpotItemRevisionCreate = {
        ...latestRevision,
        ...filteredData
      };

      // websiteをxlinksに追加
      if (selectedFields.websiteUri && details.website) {
        const existingXlinks = latestRevision?.xlinks || [];
        if (!existingXlinks.includes(details.website)) {
          mergedData.xlinks = [...existingXlinks, details.website];
        }
      }

      // googleRatingをrating配列にマージ
      if (
        (selectedFields.rating || selectedFields.userRatingCount) &&
        details.rating &&
        details.rating.rating !== null &&
        details.rating.rating !== undefined &&
        details.rating.rating_count !== null &&
        details.rating.rating_count !== undefined
      ) {
        const existingRatings = latestRevision?.rating || [];
        const filteredRatings = existingRatings.filter((r) => r.source_name !== "googlemaps");
        const googleRating = {
          source_name: details.rating.source_name || "googlemaps",
          rating: details.rating.rating,
          rating_count: details.rating.rating_count
        };
        mergedData.rating = [...filteredRatings, googleRating];
      }

      // 4. 新リビジョン作成
      await createNewRevision(spot_id, mergedData);

      return {
        spotId: spot_id,
        name,
        status: "success",
        message: "取得完了"
      };
    } catch (error) {
      return {
        spotId: spot_id,
        name,
        status: "error",
        message: error instanceof Error ? error.message : "取得失敗"
      };
    }
  };

  /**
   * PlaceIDを洗い替えてスポットを更新する
   */
  const performPlaceIdRefresh = async (
    spot: SpotWithGooglePlaceId
  ): Promise<BulkOperationResult> => {
    const { spot_id, name, latest_revision_id, google_place_id } = spot;

    try {
      // 1. place_id洗い替え
      let result;
      try {
        result = await refreshPlaceId(google_place_id);
      } catch (error) {
        throw new Error(
          `place_idの洗い替えに失敗: ${error instanceof Error ? error.message : "不明なエラー"}`
        );
      }

      if (result.status === "updated") {
        // 2. 新しいplace_idの重複チェック
        const newPlaceId = result.new_place_id;

        // 新しいplace_idからcidを取得
        const newCid = await fetchCidFromPlaceId(newPlaceId);

        // カスタムフックで重複チェック（place_id + cid）
        const existingSpotId = await checkPlaceDuplicate(newPlaceId, newCid, spot_id);

        // 他のスポットで既に使用されているかチェック
        if (existingSpotId) {
          return {
            spotId: spot_id,
            name,
            status: "error",
            message: `更新後のplace_id/cidは既に別のスポット（${existingSpotId}）で使用されています`
          };
        }

        // 3. 最新リビジョン取得
        const latestRevision = await fetchLatestRevision(spot_id, latest_revision_id);

        // 4. place_id更新
        const updatedData: SpotItemRevisionCreate = {
          ...latestRevision,
          google_place_id: newPlaceId
        };

        // 5. 新リビジョン作成
        await createNewRevision(spot_id, updatedData);

        return {
          spotId: spot_id,
          name,
          status: "success",
          message: `更新: ${google_place_id} → ${newPlaceId}`,
          oldPlaceId: google_place_id,
          newPlaceId: newPlaceId
        };
      } else if (result.status === "unchanged") {
        return {
          spotId: spot_id,
          name,
          status: "unchanged",
          message: "変更なし"
        };
      } else if (result.status === "not_found") {
        return {
          spotId: spot_id,
          name,
          status: "not_found",
          message: "Google側で無効（閉業の可能性）"
        };
      }

      // unreachable but for type safety
      return {
        spotId: spot_id,
        name,
        status: "error",
        message: "不明なステータス"
      };
    } catch (error) {
      return {
        spotId: spot_id,
        name,
        status: "error",
        message: error instanceof Error ? error.message : "更新失敗"
      };
    }
  };

  return {
    performGoogleDetailsUpdate,
    performPlaceIdRefresh
  };
};
